/**
 * Consommation de l'assistant inclus (C18) — types partagés bureau/mobile et
 * libellés humains. Module pur (aucune dépendance au store).
 */

export interface AssistantUsageInfo {
    limit: number;
    used: number;
    /** ISO du 1er du mois suivant, 00:00 UTC */
    resetsOn: string;
}

export interface AssistantPlanInfo {
    tier: 'free' | 'plus';
    /** « Découverte », « Plus » — fourni par le serveur, jamais recalculé ici */
    label: string;
}

export type AssistantSource = 'own' | 'included';

/** Ce que `ownKeyAllowed` lit de la configuration : provenance et forfait, rien d'autre. */
export interface OwnKeyEligibilityInput {
    configured?: boolean;
    source?: AssistantSource | null;
    plan?: AssistantPlanInfo;
}

/**
 * La clé propre est une option du forfait Plus (C18 SP3) : les entrées « Ma
 * clé », « Ajouter ma propre clé » et « Ajouter ma clé » ne s'affichent qu'à
 * ce forfait. Une clé propre déjà active (`own`) reste gérable. Sans forfait
 * lu (ancienne réponse, configuration non chargée), on ne cache rien à tort.
 */
export function ownKeyAllowed(config: OwnKeyEligibilityInput | null | undefined): boolean {
    if (!config) return true;
    if (config.source === 'own') return true;
    if (!config.plan) return true;
    return config.plan.tier === 'plus';
}

/** Introduction de Paramètres › Assistant : la clé propre n'est évoquée que si elle est permise (SP3). */
export function assistantIntro(config: OwnKeyEligibilityInput | null | undefined): string {
    const base = 'L\'assistant répond d\'après vos notes. Votre forfait fixe le nombre de questions par mois';
    return ownKeyAllowed(config) ? `${base} ; avec votre propre clé, il n'y a pas de limite.` : `${base}.`;
}

const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** « 1er octobre » depuis la date ISO de renouvellement (lue en UTC). Chaîne vide si invalide. */
export function renewalDayLabel(resetsOn: string): string {
    const d = new Date(resetsOn);
    if (Number.isNaN(d.getTime())) return '';
    const day = d.getUTCDate();
    return `${day === 1 ? '1er' : String(day)} ${MONTHS_FR[d.getUTCMonth()]}`;
}

export function remainingQuestions(usage: AssistantUsageInfo): number {
    return Math.max(0, usage.limit - usage.used);
}

/** Le contingent du mois est atteint : plus aucune question incluse. */
export function isQuotaReached(usage: AssistantUsageInfo): boolean {
    return usage.used >= usage.limit;
}

/** « 23 questions restantes ce mois-ci » — ligne discrète sous le composeur du bureau. */
export function remainingLabel(usage: AssistantUsageInfo): string {
    const n = remainingQuestions(usage);
    if (n === 0) return 'Aucune question restante ce mois-ci';
    if (n === 1) return '1 question restante ce mois-ci';
    return `${n} questions restantes ce mois-ci`;
}

/** « 7 questions posées sur 30 » — Paramètres bureau et Réglages mobile. */
export function usedLabel(usage: AssistantUsageInfo): string {
    const n = Math.max(0, usage.used);
    if (n === 0) return `Aucune question posée sur ${usage.limit}`;
    if (n === 1) return `1 question posée sur ${usage.limit}`;
    return `${n} questions posées sur ${usage.limit}`;
}

/** « Renouvellement le 1er octobre » ; repli sans date si `resetsOn` est invalide. */
export function renewalLabel(usage: AssistantUsageInfo): string {
    const day = renewalDayLabel(usage.resetsOn);
    return day ? `Renouvellement le ${day}` : 'Renouvellement le mois prochain';
}

/**
 * Phrase du contingent atteint, partagée par l'encart du bureau et le message
 * mobile (`sendErrorCopy`, qui y ajoute le renvoi vers le bureau). Sans
 * détails (ancienne réponse, erreur mal typée), la phrase reste correcte.
 */
export function quotaReachedMessage(usage?: AssistantUsageInfo): string {
    const day = usage ? renewalDayLabel(usage.resetsOn) : '';
    const posed = usage ? `vos ${usage.limit} questions du mois` : 'vos questions du mois';
    const renew = day ? `le ${day}` : 'le mois prochain';
    return `Vous avez posé ${posed}. Elles se renouvellent ${renew}.`;
}

export interface QuotaStateInput {
    source?: AssistantSource | null;
    usage?: AssistantUsageInfo;
    sendErrorCode?: string;
    sendErrorUsage?: AssistantUsageInfo;
}
export interface QuotaState { reached: boolean; usage?: AssistantUsageInfo }
/**
 * État « contingent atteint » du chat bureau (C18 SP1 bis, D3) : toujours
 * conditionné à la clé incluse — après l'ajout d'une clé propre, la
 * configuration rechargée rend `own` et l'état retombe. L'usage transporté par
 * l'erreur d'envoi prime sur celui, peut-être périmé, de la configuration.
 */
export function quotaState({ source, usage, sendErrorCode, sendErrorUsage }: QuotaStateInput): QuotaState {
    const included = source === 'included';
    const errorSaysReached = sendErrorCode === 'quota_exceeded';
    const reached = included && (errorSaysReached || (!!usage && isQuotaReached(usage)));
    return { reached, usage: errorSaysReached ? (sendErrorUsage ?? usage) : usage };
}
